<template>
  <v-card color="transparent" flat>
    <div v-if="!headless" class="text-center">
      <div class="text-h5 font-weight-bold">
        <slot name="title">
          {{ $t(title) }}
        </slot>
      </div>
      <div class="text-pre-wrap">
        <slot name="subtitle">
          {{ $t(subtitle) }}
        </slot>
      </div>
    </div>

    <v-card-text>
      <v-item-group v-model="uploadMethod">
        <v-row justify="center">
          <v-col cols="6">
            <v-item v-slot="{ active, toggle }" value="local">
              <v-card
                class="upload_method_card"
                :class="{ active }"
                @click="toggle"
                color="neutral600"
              >
                <v-img :aspect-ratio="3 / 2">
                  <div class="fill-all flex-center">
                    <div class="w100p">
                      <v-img
                        :src="getImageIcon('computer', active)"
                        max-width="33%"
                        :aspect-ratio="1"
                        contain
                        class="mx-auto"
                      ></v-img>
                      <p
                        class="text-h6 text-center"
                        :class="active ? 'primary--text' : 'neutral200--text'"
                      >
                        {{ $t('ARTWORK.EDIT.COPYWRITING.UPLOAD_FROM_LOCAL') }}
                      </p>
                    </div>
                  </div>
                </v-img>
              </v-card>
            </v-item>
          </v-col>

          <v-col cols="6">
            <v-item v-slot="{ active, toggle }" value="ipfs">
              <v-card
                class="upload_method_card"
                :class="{ active }"
                @click="toggle"
                color="neutral600"
              >
                <v-img :aspect-ratio="3 / 2">
                  <div class="fill-all flex-center">
                    <div class="w100p">
                      <v-img
                        :src="getImageIcon('ipfs', active)"
                        max-width="33%"
                        :aspect-ratio="1"
                        contain
                        class="mx-auto"
                      ></v-img>
                      <p
                        class="text-h6 text-center"
                        :class="active ? 'primary--text' : 'neutral200--text'"
                      >
                        {{ $t('ARTWORK.EDIT.COPYWRITING.IMPORT_FROM_IPFS') }}
                      </p>
                      <p class="ipfs_hint text-center primary--text">
                        {{ $t('ARTWORK.EDIT.COPYWRITING.RECOMMEND_FOR_NFT') }}
                      </p>
                    </div>
                  </div>
                </v-img>
              </v-card>
            </v-item>
          </v-col>
        </v-row>
      </v-item-group>
    </v-card-text>

    <v-expand-transition>
      <div v-if="!hideTypeRadio && uploadMethod === 'local'">
        <TypePicker v-model="workType" :readonly="hasFile" />

        <SizeBox :height="32" />
        <v-card-text>
          <p class="d-flex text-h5 font-weight-bold neutral100--text">
            {{ $t('COMMON.WORD.UPLOAD_FILE') }}
            <v-spacer></v-spacer>

            <v-btn v-if="hasFile" text @click="$reset">
              <v-icon>mdi-reload</v-icon>
              {{ $t('ARTWORK.EDIT.WORD.UPLOAD_AGAIN') }}
            </v-btn>
          </p>
          <v-expand-transition>
            <div v-if="showUploadArea">
              <v-card-text>
                <div class="flex-center">
                  <label
                    @dragover.prevent
                    @dragenter="isGrabing = true"
                    @drop.prevent="handleDrop"
                    @dragend="isGrabing = false"
                    @dragleave="isGrabing = false"
                    @click="(e) => !!previewURL && e.preventDefault()"
                    :class="!!previewURL ? 'w60p' : 'w100p'"
                    class="cursor-pointer"
                  >
                    <v-img
                      :src="previewURL"
                      :aspect-ratio="!!previewURL ? null : 24 / 9"
                      contain
                    >
                      <div
                        class="file_area fill-all flex-center"
                        :class="{ isGrabing, isError: !!fileError }"
                        v-if="!previewURL"
                      >
                        <slot name="placeholder">
                          <div class="text-center">
                            <div class="text-h6 mb-1">
                              <v-icon>mdi-plus</v-icon>
                              {{ $t('COMMON.WORD.UPLOAD_FILE') }}
                            </div>
                            <p>
                              {{ $t('ARTWORK.EDIT.COPYWRITING.SUPPORT') }}
                              {{ acceptExt }}
                              {{ $t('ARTWORK.EDIT.WORD.FORMAT_MAXIMUM') }}
                              {{ fileLimit }}MB
                            </p>
                          </div>
                        </slot>
                      </div>
                    </v-img>
                    <input
                      ref="fileInput"
                      @change="handleFileChange"
                      type="file"
                      hidden
                      :accept="acceptExt"
                    />
                  </label>
                </div>
                <v-card
                  v-if="!!fileError"
                  flat
                  color="transparent"
                  min-height="24"
                >
                  <div class="flex-center">
                    <span class="error--text">
                      {{ fileError }}
                    </span>
                  </div>
                </v-card>
              </v-card-text>
            </div>
          </v-expand-transition>

          <template v-if="workType === 'video'">
            <v-card-text>
              <p class="text-center">
                {{
                  $t('ARTWORK.EDIT.COPYWRITING.ONLY_SUPPORTS_YOUTUBE_VIDEO_URL')
                }}
              </p>

              <v-text-field
                :label="$t('ARTWORK.EDIT.WORD.VIDEO_URL')"
                :placeholder="
                  $t(
                    'ARTWORK.EDIT.INPUT.COPYWRITING.PLEASE_PASTE_YOUR_YOUTUBE_VIDEO_URL'
                  )
                "
                v-model="videoURL"
                :rules="[$rules.required, checkYTURL]"
                counter="128"
                background-color="neutral700"
                outlined
              />
              <!-- 、Vimeo、bilibili、优酷 -->
            </v-card-text>
            <div class="iframe-container">
              <iframe
                v-if="
                  checkYTURL(videoURL) !==
                    $t('ARTWORK.EDIT.INPUT.ALERT.INVALID_URL_FORMAT') &&
                  $rules.required(videoURL)
                "
                :src="translatedYoutubeUrl(videoURL)"
                frameborder="0"
                allowFullScreen
              ></iframe>
            </div>
          </template>

          <template v-if="workType === 'website'">
            <v-card-text>
              <v-text-field
                :label="$t('ARTWORK.EDIT.WORD.WEBPAGE_URL')"
                v-model="websiteURL"
                :rules="[$rules.required, $rules.url]"
                background-color="neutral700"
                outlined
              />
              <!-- 、Vimeo、bilibili、优酷 -->
            </v-card-text>
            <div class="iframe-container">
              <iframe
                v-if="$rules.url(websiteURL) && $rules.required(websiteURL)"
                :src="websiteURL"
                frameborder="0"
                allowFullScreen
              ></iframe>
            </div>
          </template>

          <div
            v-if="
              workType === 'sound' &&
              (artworkData.artworkMusicUrl || holdingFile)
            "
          >
            <AudioPlayer
              :step="step"
              :src="artworkData.artworkMusicUrl || previewURL"
              :file="holdingFile"
              :file-name="
                artworkData.authorName || (holdingFile && holdingFile.name)
              "
            />
          </div>

          <div
            v-if="
              workType === '3dmodel' &&
              (!!previewURL || !!artworkData.artworkModelUrl)
            "
          >
            <v-lazy class="d-flex align-center">
              <ModelViewer
                @error="previewURL = null"
                :model="
                  isGLTF
                    ? holdingFile
                    : previewURL || artworkData.artworkModelUrl
                "
                content-class="b-rad-16"
              />
            </v-lazy>
          </div>
        </v-card-text>
      </div>
    </v-expand-transition>

    <v-expand-transition>
      <div v-if="method === 'ipfs'">
        <SizeBox :height="32" />

        <v-text-field
          v-show="!hasPreview"
          :label="$t('ARTWORK.EDIT.WORD.IPFS_URL')"
          v-model="ipfsLink"
          :rules="[$rules.required, $rules.url]"
          background-color="neutral700"
          outlined
          @input="$emit('update:analyzed', false)"
        >
          <template #append>
            <v-btn
              depressed
              small
              color="primary"
              @click="() => checkIpfsType()"
            >
              {{ $t('ARTWORK.EDIT.WORD.VALIDATE') }}
            </v-btn>
          </template>
        </v-text-field>

        <p class="mb-0 error--text" v-if="analyzed && isAnalyzeFailed">
          {{ $t('ARTWORK.EDIT.COPYWRITING.PLEASE_SELECT_A_TYPE') }}
        </p>
        <div v-if="(!!ipfsLink && analyzed) || hasPreview">
          <TypePicker
            v-model="workType"
            :readonly="!isAnalyzeFailed"
            :hide="[]"
          />
          <SizeBox :height="32" />
          <template v-if="!isAnalyzeFailed">
            <div v-if="workType === 'image'">
              <v-img
                :src="previewURL || ipfsLink"
                width="80%"
                class="mx-auto"
              ></v-img>
            </div>
            <div v-else-if="workType === 'video'" class="flex-center">
              <video
                :src="videoURL || ipfsLink"
                width="80%"
                class="mx-auto"
                controls
              ></video>
            </div>
            <div v-else-if="workType === 'sound'">
              <AudioPlayer
                :step="step"
                :src="artworkData.artworkMusicUrl || ipfsLink"
              />
            </div>
          </template>
          <v-lazy v-if="workType === '3dmodel'" class="flex-center">
            <ModelViewer
              v-if="!!previewURL || !!ipfsLink"
              :model="previewURL || ipfsLink"
              @error="
                $emit('update:analyzed', false);
                previewURL = null;
              "
              content-class="b-rad-16"
              need-parse
            />
          </v-lazy>
        </div>

        <div class="flex-center">
          <v-btn v-show="hasPreview" text @click="$reset">
            <v-icon>mdi-reload</v-icon>
            {{ $t('ARTWORK.EDIT.WORD.UPLOAD_AGAIN') }}
          </v-btn>
        </div>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import ModelViewer from '@/components/ModelViewer.vue';
import TypePicker from './TypePicker.vue';
import AudioPlayer from '@/components/AudioPlayer.vue';
const isUseFile = (type) => ['image', 'sound', '3dmodel'].includes(type);

export default {
  components: { TypePicker, ModelViewer, AudioPlayer },
  props: {
    step: {
      type: Number,
      default: 1
    },
    passingData: {
      type: Object,
      default: () => ({})
    },
    headless: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'ARTWORK.EDIT.WORD.UPLOAD_ITEM'
    },
    subtitle: {
      type: String,
      default: 'ARTWORK.EDIT.COPYWRITING.SELECT_METHOD_AND_ITEM_TYPE'
      // ，\n如是 NFT 作品的話推薦使用 IPFS 的上傳方式'
    },
    fileLimit: {
      type: Number,
      default: 5
    },
    method: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: ''
    },
    hideTypeRadio: {
      type: Boolean,
      default: false
    },
    URL: {
      type: String,
      default: null
    },
    passedIpfsLink: {
      type: String,
      default: null
    },
    value: {
      type: [Object, String, File, Blob],
      default: null
    },
    img: {
      type: String,
      default: ''
    },
    rules: {
      type: Array, // Array<Function>
      default: () => [],
      validator(v) {
        if (v.length) {
          return !v.some((_v) => typeof _v !== 'function');
        } else {
          return true;
        }
      }
    },
    analyzed: {
      type: Boolean,
      default: false
    },
    analyzing: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // common

      workType: this.type,
      holdingFile: null, // only for: image, sound, 3dmodel
      uploadMethod: this.method, // local, ipfs
      isGrabing: false,
      fileError: null,
      isUploadingPreview: false,
      previewedImageName: '',
      workData: {},

      // image
      previewURL: this.img,

      // video
      videoURL: this.URL,

      // sound

      // 3d model

      // website
      websiteURL: this.URL,

      // ipfs
      ipfsLink: this.passedIpfsLink,
      isAnalyzeFailed: false
    };
  },

  computed: {
    artworkData() {
      return this.workData.data || {};
    },
    hasFile() {
      return !!this.holdingFile || !!this.workData.data;
    },
    hasPreview() {
      return !!this.previewURL || !!this.videoURL;
    },
    showUploadArea() {
      switch (this.workType) {
        case 'image':
          return true;
        case 'sound':
          return !this.hasFile;
        case '3dmodel':
          return !this.hasFile;

        default:
          return false;
      }
    },
    acceptExt() {
      switch (this.workType) {
        case 'image':
          return '.jpg,.jpeg,.png';
        case 'video':
          return '';
        case 'sound':
          return '.mp3,.m4a';
        case '3dmodel':
          return '.glb,.gltf';
        case 'website':
          return '';

        default:
          return '.jpg,.jpeg,.png';
      }
    },
    isGLTF() {
      return this?.holdingFile?.name?.endsWith('.gltf');
    }
  },

  watch: {
    method(v) {
      this.uploadMethod = v;
    },
    uploadMethod(v) {
      this.$emit('update-method', v);
    },
    type(v) {
      this.workType = v;
    },
    workType(v) {
      this.fileError = null;
      this.holdingFile = null;
      this.videoURL = null;
      this.websiteURL = null;
      this.$emit('update-type', v);
    },
    previewURL(v) {
      this.fileError = null;
      if (!v) {
        this.$refs.fileInput && (this.$refs.fileInput.value = null);
        this.holdingFile = null;
      }
      this.$emit('update-URL', v);
    },
    videoURL(v) {
      this.$emit('update-video-URL', v);
    },
    websiteURL(v) {
      this.$emit('update-website-URL', v);
    },
    holdingFile(v) {
      if (!v) {
        this.previewURL = null;
      }
      if (isUseFile(this.workType)) this.$emit('input', v);
    },
    ipfsLink(v) {
      this.workType = '';
      this.$emit('update:passedIpfsLink', v);
    }
  },

  mounted() {
    this.$set(this, 'workData', window.structuredClone(this.passingData));
  },

  methods: {
    $reset() {
      this.previewURL = null;
      this.fileError = null;
      this.holdingFile = null;
      this.videoURL = null;
      this.websiteURL = null;
      this.ipfsLink = null;
      this.$set(this, 'workData', {});
    },
    getImageIcon(name, active) {
      return require(`@/assets/icons/${name}${active ? '_active' : ''}.svg`);
    },
    $validate() {
      for (let i = 0; i < this.rules.length; i++) {
        const rule = this.rules[i];

        if (rule && typeof rule === 'function') {
          const result = rule(
            this.value ||
              this.videoURL ||
              this.websiteURL ||
              this.artworkData.artworkModelUrl ||
              this.artworkData.artworkMusicUrl
          );
          this.fileError = result === true ? null : result;
        } else {
          this.fileError = null;
        }
      }

      return this.fileError === null;
    },
    handleDrop(e) {
      this.isGrabing = false;
      if (e.dataTransfer.files.length) {
        const file = e.dataTransfer.files[0];
        const exts = this.acceptExt.replaceAll('.', '').split(',');

        if (exts.includes(file.name.split('.').pop())) {
          this.handlePreview(file);
        } else {
          this.$toast.error('ARTWORK.EDIT.TOAST.ALERT.FILE_FORMAT_NOT_MATCH');
        }
      }
    },
    handleFileChange(e) {
      if (e.target.files.length) {
        const file = e.target.files[0];
        this.handlePreview(file);
      }
    },

    handlePreview(file, isIpfs) {
      if (file.size > 1048576 * this.fileLimit && !isIpfs) {
        this.$toast.error(
          `${this.$t('ARTWORK.EDIT.COPYWRITING.FILE_SIZE_NOT_EXCEED')} ${
            this.fileLimit
          }MB`
        );
        return;
      }

      this.holdingFile = file;
      const FR = new FileReader();
      FR.onload = async (e) => {
        this.previewURL = FR.result;

        const Result = await this.$shrinkImg(this.previewURL, {
          targetSize: 1048576 * this.fileLimit
        });

        this.holdingFile = Result.tests ? Result.file : file;
        this.previewURL = Result.imgURL;
      };

      if (file) {
        FR.readAsDataURL(file);
      }
    },

    // video
    checkYTURL(text) {
      const urlReg =
        /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)?/;
      const isUrl = urlReg.test(text);
      const hasYoutube = /youtube.com|youtu.be\//.test(text);

      return (
        (isUrl && hasYoutube) ||
        this.$t('ARTWORK.EDIT.INPUT.ALERT.INVALID_URL_FORMAT')
      );
    },

    // ipfs
    async checkIpfsType(v = this.ipfsLink) {
      if (!v) return;
      if (this.$rules.url(v) !== true) return;
      this.$emit('update:analyzed', false);
      this.$emit('update:analyzing', true);
      this.isAnalyzeFailed = false;
      // https://assets.akaswap.com/ipfs/QmQYnbN5ZAkDkUD6VQ2KJxSCiVLiEqDpkXFCDZcb5HUYUD video
      // https://assets.akaswap.com/ipfs/QmNfwQroVvaY5hj4Kc7HGnwHh4vxaJPnv7aSkeZHsDVJBx image
      // https://assets.akaswap.com/ipfs/Qmdx3XNpn2NBo8MY7njHqTbn3oTFDmXvMXpuHeWAeKSuPr 3d_modle
      // https://assets.akaswap.com/ipfs/QmWuNvwuMuoRTx9b8vodvBmE8dJBHbvACF9T9EHDGZwAzY audio

      try {
        const res = await fetch(v);
        await this.analyzeIpfs(res);
      } catch (error) {
        this.isAnalyzeFailed = true;
        console.error(error);
      } finally {
        this.$emit('update:analyzed', true);
        this.$emit('update:analyzing', false);
      }
    },

    async analyzeIpfs(res) {
      const contentType = res.headers.get('content-type');
      switch (true) {
        case /image/.test(contentType):
          this.workType = 'image';
          break;
        case /video/.test(contentType):
          this.workType = 'video';
          break;
        case /audio/.test(contentType):
          this.workType = 'sound';
          break;
        case /model/.test(contentType):
          this.workType = '3dmodel';
          break;

        default:
          this.isAnalyzeFailed = true;
          break;
      }

      if (this.workType === 'image') {
        const img = await res.blob();
        this.holdingFile = img;
        this.handlePreview(img, true);
      }
    },
    translatedYoutubeUrl(url) {
      if (!url) {
        return;
      }
      const regExp =
        /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      let embedId = match && match[2].length === 11 ? match[2] : null;
      if (!embedId && url.includes('shorts')) {
        const regex =
          /(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/gm;
        embedId = regex.exec(url)[3];
      }
      return `https://www.youtube.com/embed/${embedId}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.file_area {
  * {
    user-select: none;
  }
  position: relative;
  border: 1px dashed;
  border-color: $neutral300;
  border-radius: 6px;
  background-color: $neutral700;

  &.isError {
    border-color: $error;
  }

  &.isGrabing {
    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      width: calc(100% - 8px);
      height: calc(100% - 8px);
      background-color: rgba($neutral100, $alpha: 0.6);
      border-radius: 6px;
      border: 2px solid $primary;
    }
  }
}

.upload_method_card {
  &.active {
    border-width: 2px !important;
    border-style: solid !important;
    border-color: $primary !important;
  }

  .ipfs_hint {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
  }
}
.iframe-container {
  margin: 16px;
  position: relative;
  padding-bottom: 56.25%; // This is the aspect ratio
  overflow: hidden;
  iframe {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
}
</style>